import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import { SITE_URL } from "@/lib/siteConfig";
import {
  Mail, Calendar, ArrowRight, ShoppingCart, Monitor, Home,
  Wrench, GraduationCap, Building2, HeartPulse, Globe,
  Sparkles, Download, Check, Clock, Zap, Star,
  Target, ChevronDown, ChevronUp, Play,
} from "lucide-react";

// ── Campaign data for the public page ──
const CAMPAIGNS = [
  { slug: "ecom-welcome-first-purchase", name: "Welcome & First Purchase", desc: "Convert new subscribers into first-time buyers.", industry: "ecommerce", emails: 5, days: 7, category: "welcome" },
  { slug: "ecom-abandoned-cart", name: "Abandoned Cart Recovery", desc: "Recover lost revenue with a proven 3-email sequence.", industry: "ecommerce", emails: 3, days: 3, category: "recovery" },
  { slug: "ecom-post-purchase-upsell", name: "Post-Purchase Upsell", desc: "Turn one-time buyers into repeat customers.", industry: "ecommerce", emails: 4, days: 14, category: "retention" },
  { slug: "ecom-seasonal-flash-sale", name: "Seasonal Flash Sale", desc: "Drive massive revenue with a flash sale sequence.", industry: "ecommerce", emails: 5, days: 5, category: "promotion" },
  { slug: "ecom-win-back", name: "Win-Back & Re-engagement", desc: "Bring inactive customers back.", industry: "ecommerce", emails: 3, days: 10, category: "re-engagement" },
  { slug: "saas-trial-onboarding", name: "Free Trial Onboarding", desc: "Guide trial users to their 'aha moment'.", industry: "saas", emails: 7, days: 14, category: "onboarding" },
  { slug: "saas-product-launch", name: "Product Launch Announcement", desc: "Build hype and drive adoption for a new feature.", industry: "saas", emails: 4, days: 7, category: "launch" },
  { slug: "saas-churn-prevention", name: "Churn Prevention", desc: "Save at-risk customers before they cancel.", industry: "saas", emails: 3, days: 7, category: "retention" },
  { slug: "saas-annual-upgrade", name: "Annual Plan Upgrade", desc: "Convert monthly subscribers to annual plans.", industry: "saas", emails: 3, days: 5, category: "upsell" },
  { slug: "realestate-new-listing", name: "New Listing Drip", desc: "Generate buzz for a new property listing.", industry: "real_estate", emails: 4, days: 10, category: "listing" },
  { slug: "realestate-buyer-nurture", name: "Buyer Nurture Sequence", desc: "Build trust with prospective home buyers.", industry: "real_estate", emails: 5, days: 21, category: "nurture" },
  { slug: "local-new-customer-welcome", name: "New Customer Welcome", desc: "Make a great first impression with new customers.", industry: "local_services", emails: 3, days: 7, category: "welcome" },
  { slug: "local-seasonal-booking", name: "Seasonal Booking Push", desc: "Fill your calendar during peak season.", industry: "local_services", emails: 4, days: 10, category: "promotion" },
  { slug: "edu-lead-magnet-nurture", name: "Free Lead Magnet Nurture", desc: "Convert freebie downloaders into paying students.", industry: "education", emails: 5, days: 10, category: "nurture" },
  { slug: "edu-course-launch", name: "Course Launch Sequence", desc: "Launch a course with a 6-email story-driven sequence.", industry: "education", emails: 6, days: 7, category: "launch" },
  { slug: "agency-cold-outreach", name: "Cold Outreach Warm-Up", desc: "Warm up cold leads with value-first B2B emails.", industry: "agency", emails: 4, days: 12, category: "outreach" },
  { slug: "agency-client-onboarding", name: "Client Onboarding", desc: "Set new agency clients up for success.", industry: "agency", emails: 4, days: 14, category: "onboarding" },
  { slug: "health-new-patient-welcome", name: "New Patient Welcome", desc: "Welcome new patients and prep for first visit.", industry: "health", emails: 4, days: 14, category: "welcome" },
  { slug: "health-wellness-challenge", name: "7-Day Wellness Challenge", desc: "Engage members with a wellness challenge.", industry: "health", emails: 5, days: 7, category: "engagement" },
  { slug: "universal-newsletter-reengagement", name: "Newsletter Re-engagement", desc: "Clean your list and re-engage inactive subscribers.", industry: "general", emails: 3, days: 7, category: "re-engagement" },
];

const INDUSTRIES = [
  { value: "",              label: "All",            icon: Globe,         color: "text-slate" },
  { value: "ecommerce",     label: "E-commerce",     icon: ShoppingCart,  color: "text-coral" },
  { value: "saas",          label: "SaaS",           icon: Monitor,       color: "text-blue-600" },
  { value: "real_estate",   label: "Real Estate",    icon: Home,          color: "text-emerald-600" },
  { value: "local_services",label: "Local Services", icon: Wrench,        color: "text-amber-700" },
  { value: "education",     label: "Education",      icon: GraduationCap, color: "text-purple-600" },
  { value: "agency",        label: "Agency / B2B",   icon: Building2,     color: "text-indigo-600" },
  { value: "health",        label: "Health",         icon: HeartPulse,    color: "text-rose-600" },
  { value: "general",       label: "Universal",      icon: Sparkles,      color: "text-teal" },
];

const INDUSTRY_COLOR: Record<string, string> = {
  ecommerce: "border-coral/30 bg-coral-light/30",
  saas: "border-blue-200 bg-blue-50/30",
  real_estate: "border-emerald-200 bg-emerald-50/30",
  local_services: "border-amber-200 bg-amber-50/30",
  education: "border-purple-200 bg-purple-50/30",
  agency: "border-indigo-200 bg-indigo-50/30",
  health: "border-rose-200 bg-rose-50/30",
  general: "border-teal/20 bg-teal-light/30",
};

const JSON_LD = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "MoreMail Prebuilt Campaign Library",
  applicationCategory: "BusinessApplication",
  description: "20+ ready-to-send email campaign sequences for e-commerce, SaaS, real estate, and more. Just customize and send.",
  offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
};

export default function PrebuiltCampaignsLandingPage() {
  const [filter, setFilter] = useState("");
  const [visibleCount, setVisibleCount] = useState(9);

  const filtered = filter ? CAMPAIGNS.filter(c => c.industry === filter) : CAMPAIGNS;
  const visible = filtered.slice(0, visibleCount);
  const totalEmails = CAMPAIGNS.reduce((sum, c) => sum + c.emails, 0);

  return (
    <>
      <SeoHead
        title="Pre-Built Email Campaigns — Ready to Send Sequences for Every Industry | MoreMail"
        description={`Browse ${CAMPAIGNS.length}+ expert-crafted email campaign sequences for e-commerce, SaaS, real estate, health & more. ${totalEmails} emails ready to customize and send.`}
        canonical={`${SITE_URL}/prebuilt-campaigns`}
        keywords="prebuilt email campaigns, email sequence templates, ready to send email campaigns, email marketing templates, drip campaign templates, welcome email sequence, abandoned cart emails"
        ogTitle={`${CAMPAIGNS.length} Pre-Built Email Campaigns — Plug In and Send | MoreMail`}
        ogDescription="Expert-crafted email sequences with subjects, copy, and CTAs ready to go. Just add your brand and send."
        jsonLd={JSON_LD}
      />

      {/* ── Hero Section ── */}
      <section className="pt-16 pb-20 bg-gradient-to-b from-ink to-[#162029] text-center relative overflow-hidden">
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-[0.04]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "40px 40px" }}
        />

        <div className="max-w-4xl mx-auto px-6 relative z-10">
          <div className="inline-flex items-center gap-2 bg-white/10 text-white/80 px-4 py-1.5 rounded-full text-xs font-semibold mb-6 backdrop-blur-sm">
            <Sparkles size={13} className="text-coral" />
            {CAMPAIGNS.length} campaigns • {totalEmails} emails • 8 industries
          </div>

          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5">
            Pre-Built Email Campaigns<br />
            <span className="text-coral">Ready to Send</span>
          </h1>

          <p className="text-white/60 text-lg max-w-2xl mx-auto mb-8 leading-relaxed">
            Expert-crafted email sequences with proven copy, subject lines, and send schedules.
            Pick a campaign, add your brand, and start sending in minutes — not days.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/register"
              className="inline-flex items-center justify-center gap-2 bg-coral hover:bg-coral-dark text-white px-7 py-3.5 rounded-lg text-sm font-bold transition-colors shadow-lg shadow-coral/20"
            >
              Start Free — Browse Library <ArrowRight size={15} />
            </Link>
            <a
              href="#campaigns"
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-7 py-3.5 rounded-lg text-sm font-semibold transition-colors backdrop-blur-sm"
            >
              <Play size={14} /> Preview Campaigns
            </a>
          </div>
        </div>
      </section>

      {/* ── Stats Bar ── */}
      <section className="bg-white border-b border-sand">
        <div className="max-w-5xl mx-auto px-6 py-8 grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
          {[
            { value: `${CAMPAIGNS.length}+`, label: "Campaign Templates", icon: Mail },
            { value: `${totalEmails}`, label: "Emails Written", icon: Zap },
            { value: "8", label: "Industries Covered", icon: Target },
            { value: "< 5 min", label: "Setup Time", icon: Clock },
          ].map((stat) => (
            <div key={stat.label}>
              <stat.icon size={20} className="mx-auto text-coral mb-2" />
              <p className="font-display font-bold text-ink text-2xl">{stat.value}</p>
              <p className="text-xs text-slate mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-16 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-10">
            Three Steps to <span className="text-coral">Email Marketing Done</span>
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "1", title: "Pick a Campaign", desc: "Browse our library and choose a pre-built sequence that fits your business.", icon: Target },
              { step: "2", title: "Add Your Brand", desc: "Enter your brand name and CTA link. The emails are already written — you just personalize.", icon: Sparkles },
              { step: "3", title: "Send & Grow", desc: "Review the draft emails, tweak if you want, and start sending to your audience.", icon: Mail },
            ].map((s) => (
              <div key={s.step} className="bg-white rounded-xl p-6 border border-sand text-center">
                <div className="w-10 h-10 bg-coral text-white rounded-full flex items-center justify-center font-bold text-sm mx-auto mb-4">
                  {s.step}
                </div>
                <h3 className="font-display font-bold text-ink mb-2">{s.title}</h3>
                <p className="text-sm text-slate leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Campaign Library ── */}
      <section id="campaigns" className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-3">
            Browse the <span className="text-coral">Campaign Library</span>
          </h2>
          <p className="text-slate text-center text-sm mb-8 max-w-xl mx-auto">
            Every campaign includes complete email copy, subject lines, preview text, CTAs, and send schedules.
          </p>

          {/* Industry filter pills */}
          <div className="flex flex-wrap justify-center gap-2 mb-8">
            {INDUSTRIES.map((ind) => {
              const Icon = ind.icon;
              const active = filter === ind.value;
              return (
                <button
                  key={ind.value}
                  onClick={() => { setFilter(ind.value); setVisibleCount(9); }}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-full text-xs font-semibold transition-all border ${
                    active
                      ? "bg-ink text-white border-ink"
                      : "bg-white border-sand text-slate hover:border-ink/30 hover:text-ink"
                  }`}
                >
                  <Icon size={13} /> {ind.label}
                </button>
              );
            })}
          </div>

          {/* Campaign cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map((c) => {
              const ind = INDUSTRIES.find(i => i.value === c.industry) || INDUSTRIES[0];
              const Icon = ind.icon;
              return (
                <div
                  key={c.slug}
                  className={`rounded-xl border p-5 transition-all hover:shadow-md ${INDUSTRY_COLOR[c.industry] || "border-sand bg-white"}`}
                >
                  <div className="flex items-center gap-1.5 mb-2">
                    <Icon size={13} className={ind.color} />
                    <span className={`text-[10px] font-semibold uppercase tracking-wide ${ind.color}`}>
                      {ind.label}
                    </span>
                  </div>
                  <h3 className="font-display font-bold text-ink text-base mb-1">{c.name}</h3>
                  <p className="text-xs text-slate leading-relaxed mb-3">{c.desc}</p>
                  <div className="flex items-center gap-4 text-[11px] text-slate">
                    <span className="flex items-center gap-1"><Mail size={11} /> {c.emails} emails</span>
                    <span className="flex items-center gap-1"><Calendar size={11} /> {c.days} days</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Show more */}
          {visible.length < filtered.length && (
            <div className="text-center mt-6">
              <button
                onClick={() => setVisibleCount(filtered.length)}
                className="inline-flex items-center gap-2 text-sm text-coral font-semibold hover:underline"
              >
                Show all {filtered.length} campaigns <ChevronDown size={14} />
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ── What's Included ── */}
      <section className="py-16 bg-paper">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-10">
            Every Campaign Includes
          </h2>
          <div className="grid md:grid-cols-2 gap-4 max-w-3xl mx-auto">
            {[
              "Compelling subject lines optimized for opens",
              "Preview text that drives inbox curiosity",
              "Professional email body copy — ready to send",
              "Strategic send schedule (day + time)",
              "Clear goals for every email in the sequence",
              "CTA buttons with conversion-focused text",
              "Personalization tokens ({{first_name}}, etc.)",
              "Works with any email sending setup",
            ].map((item) => (
              <div key={item} className="flex items-start gap-3 bg-white rounded-lg p-4 border border-sand">
                <Check size={16} className="text-teal shrink-0 mt-0.5" />
                <span className="text-sm text-ink">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Industry Breakdown ── */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="font-display text-2xl md:text-3xl font-bold text-ink text-center mb-3">
            Built for <span className="text-coral">Your Industry</span>
          </h2>
          <p className="text-sm text-slate text-center mb-10 max-w-lg mx-auto">
            Each campaign is crafted with industry-specific language, psychology, and best practices.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {INDUSTRIES.filter(i => i.value).map((ind) => {
              const Icon = ind.icon;
              const count = CAMPAIGNS.filter(c => c.industry === ind.value).length;
              const emailCount = CAMPAIGNS.filter(c => c.industry === ind.value).reduce((s, c) => s + c.emails, 0);
              return (
                <button
                  key={ind.value}
                  onClick={() => { setFilter(ind.value); document.getElementById("campaigns")?.scrollIntoView({ behavior: "smooth" }); }}
                  className="text-left bg-paper rounded-xl p-5 border border-sand hover:border-coral/30 hover:shadow-sm transition-all group"
                >
                  <Icon size={22} className={`${ind.color} mb-3`} />
                  <h3 className="font-display font-bold text-ink text-sm group-hover:text-coral transition-colors">{ind.label}</h3>
                  <p className="text-xs text-slate mt-1">{count} campaigns • {emailCount} emails</p>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── Final CTA ── */}
      <section className="py-20 bg-ink text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "32px 32px" }}
        />
        <div className="max-w-2xl mx-auto px-6 relative z-10">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-4">
            Stop Writing Emails From Scratch
          </h2>
          <p className="text-white/60 text-base mb-8 leading-relaxed">
            {totalEmails} professionally written emails across {CAMPAIGNS.length} proven campaign sequences.
            Pick one, add your brand, and start sending today.
          </p>
          <Link
            to="/register"
            className="inline-flex items-center gap-2 bg-coral hover:bg-coral-dark text-white px-8 py-4 rounded-lg font-bold transition-colors shadow-lg shadow-coral/20"
          >
            Get Started Free <ArrowRight size={16} />
          </Link>
          <p className="text-white/30 text-xs mt-4">No credit card required • Free plan includes 1,000 subscribers</p>
        </div>
      </section>
    </>
  );
}
